import React, { Suspense, lazy } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { WhatsAppButton } from './components/WhatsAppButton';
import { Toaster } from './components/ui/sonner';

// Lazy load route components for better performance
const Home = lazy(() => import('./pages/Home').then(module => ({ default: module.Home })));
const Services = lazy(() => import('./pages/Services').then(module => ({ default: module.Services })));
const ServiceDetail = lazy(() => import('./pages/Services').then(module => ({ default: module.ServiceDetail })));
const Gallery = lazy(() => import('./pages/Gallery').then(module => ({ default: module.Gallery })));
const Legislation = lazy(() => import('./pages/Legislation').then(module => ({ default: module.Legislation })));
const Contact = lazy(() => import('./pages/Contact').then(module => ({ default: module.Contact })));
const Blog = lazy(() => import('./pages/Blog').then(module => ({ default: module.Blog })));
const BlogPost = lazy(() => import('./pages/BlogPost').then(module => ({ default: module.BlogPost })));
const StaffLogin = lazy(() => import('./pages/StaffLogin').then(module => ({ default: module.StaffLogin })));
const StaffDashboard = lazy(() => import('./pages/StaffDashboard').then(module => ({ default: module.StaffDashboard })));

// Lazy load policy pages
const QualityPolicy = lazy(() => import('./pages/policies').then(module => ({ default: module.QualityPolicy })));
const QMSScope = lazy(() => import('./pages/policies').then(module => ({ default: module.QMSScope })));
const ComplaintsProcedure = lazy(() => import('./pages/policies').then(module => ({ default: module.ComplaintsProcedure })));
const PrivacyPolicy = lazy(() => import('./pages/policies').then(module => ({ default: module.PrivacyPolicy })));
const TermsConditions = lazy(() => import('./pages/policies').then(module => ({ default: module.TermsConditions })));
const ComplianceImprovement = lazy(() => import('./pages/policies').then(module => ({ default: module.ComplianceImprovement })));
const GDPRPolicy = lazy(() => import('./pages/policies').then(module => ({ default: module.GDPRPolicy })));

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#4a7c59]"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

const Layout = ({ children }) => {
  const location = useLocation();
  const isStaffPage = location.pathname.startsWith('/staff');

  if (isStaffPage) {
    return <>{children}</>;
  }

  return (
    <>
      <Header />
      {children}
      <Footer />
      <WhatsAppButton />
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <Layout>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/:slug" element={<ServiceDetail />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/legislation" element={<Legislation />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/staff/login" element={<StaffLogin />} />
                <Route path="/staff/dashboard" element={<StaffDashboard />} />
                
                {/* Policy Pages */}
                <Route path="/quality-policy" element={<QualityPolicy />} />
                <Route path="/qms-scope" element={<QMSScope />} />
                <Route path="/complaints-procedure" element={<ComplaintsProcedure />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/gdpr-policy" element={<GDPRPolicy />} />
                <Route path="/terms-conditions" element={<TermsConditions />} />
                <Route path="/compliance-improvement" element={<ComplianceImprovement />} />
              </Routes>
            </Suspense>
          </Layout>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </HelmetProvider>
  );
}

export default App;
